import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Waves, Bike, Timer, Dumbbell, Calendar, TrendingUp, ChevronRight, Check, Minus, Crown, Star, Sparkles, Quote, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSeo } from '../lib/useSeo';

const Landing = () => {
  useSeo({ title: 'Entrenador Personal de Triatlón Online | Coach Alex Díaz — TriFlow Pro', description: 'Planes de entrenamiento de triatlón 100% personalizados (Sprint, Olímpico, 70.3 e Ironman) con seguimiento diario del coach, análisis de carga y app propia. 12 años entrenando a triatletas amateur y élite.', path: '/' });

  const API = process.env.REACT_APP_BACKEND_URL;
  const [lead, setLead] = useState({
    full_name: '',
    email: '',
    phone: '',
    race_distance: 'Sprint',
    race_date: '',
    goal: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadError, setLeadError] = useState('');

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadError('');
    if (!lead.full_name.trim() || !lead.email.trim() || !lead.goal.trim()) {
      setLeadError('Rellena al menos nombre, email y objetivo.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lead)
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.detail || 'No pudimos enviar tu consulta. Intenta de nuevo.');
      }
      setSubmitted(true);
    } catch (err) {
      setLeadError(err.message || 'Error inesperado. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Waves className="w-6 h-6" />,
      title: "Natación",
      description: "Planes específicos para mejorar tu técnica y resistencia en el agua",
      color: "text-swim",
      bgColor: "bg-swim/10",
      borderColor: "border-swim/30"
    },
    {
      icon: <Bike className="w-6 h-6" />,
      title: "Ciclismo",
      description: "Entrenamientos de potencia, resistencia y técnica en bici",
      color: "text-bike",
      bgColor: "bg-bike/10",
      borderColor: "border-bike/30"
    },
    {
      icon: <Timer className="w-6 h-6" />,
      title: "Carrera",
      description: "Series, tempo runs y rodajes para mejorar tu marca",
      color: "text-run",
      bgColor: "bg-run/10",
      borderColor: "border-run/30"
    },
    {
      icon: <Dumbbell className="w-6 h-6" />,
      title: "Fuerza",
      description: "Entrenamiento de fuerza específico para triatletas",
      color: "text-[#a855f7]",
      bgColor: "bg-[#a855f7]/10",
      borderColor: "border-[#a855f7]/30"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Planificación",
      description: "Calendario inteligente con periodización automática",
      color: "text-[#f472b6]",
      bgColor: "bg-[#f472b6]/10",
      borderColor: "border-[#f472b6]/30"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Progreso",
      description: "Estadísticas detalladas y seguimiento de tu evolución",
      color: "text-[#22d3ee]",
      bgColor: "bg-[#22d3ee]/10",
      borderColor: "border-[#22d3ee]/30"
    }
  ];

  const plans = [
    { name: "Sprint", distance: "750m / 20km / 5km", weeks: "8 semanas", disciplines: ['swim','bike','run'] },
    { name: "Olímpico", distance: "1.5km / 40km / 10km", weeks: "12 semanas", disciplines: ['swim','bike','run'] },
    { name: "Half Ironman", distance: "1.9km / 90km / 21km", weeks: "16 semanas", disciplines: ['swim','bike','run'] },
    { name: "Ironman", distance: "3.8km / 180km / 42km", weeks: "24 semanas", disciplines: ['swim','bike','run'] },
    { name: "Aquabike Sprint", distance: "750m / 20km", weeks: "6 semanas", disciplines: ['swim','bike'] },
    { name: "Aquabike Olímpico", distance: "1.5km / 40km", weeks: "10 semanas", disciplines: ['swim','bike'] },
    { name: "Media Maratón", distance: "21,1km", weeks: "10 semanas", disciplines: ['run'] },
    { name: "Maratón", distance: "42,2km", weeks: "16 semanas", disciplines: ['run'] }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] overflow-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/coach-logo-dark.png" 
              alt="Coach Alex Díaz" 
              className="w-12 h-12 object-contain"
            />
            <span className="font-black text-xl tracking-tight">Coach Alex Díaz</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/coach/alex-diaz">
              <Button variant="ghost" className="text-zinc-400 hover:text-white hidden sm:inline-flex" data-testid="nav-meet-coach-btn">
                Conoce a tu coach
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="nav-login-btn">
                Iniciar Sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary" data-testid="nav-register-btn">
                Empezar Gratis
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#ccff00]/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00f0ff]/10 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto stagger-children">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#ccff00] animate-pulse"></span>
              <span className="text-sm text-zinc-400">Tu entrenamiento de triatlón, potenciado</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1] mb-6">
              Entrena como un
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00f0ff] via-[#ff4d00] to-[#ccff00]">
                Ironman
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-10">
              Planifica, registra y analiza tu entrenamiento de triatlón. Planes personalizados para Sprint, Olímpico, Half Ironman, Ironman y más.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button className="btn-primary text-lg px-8 py-6 animate-pulse-glow" data-testid="hero-cta-btn">
                  Comenzar Ahora
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="btn-secondary text-lg px-8 py-6" data-testid="hero-login-btn">
                  Ya tengo cuenta
                </Button>
              </Link>
              <Link to="/coach/alex-diaz">
                <Button variant="ghost" className="text-zinc-300 hover:text-[#ccff00] text-lg px-6 py-6 underline underline-offset-4 decoration-[#ccff00]/40 hover:decoration-[#ccff00]" data-testid="hero-meet-coach-btn">
                  Conoce a tu coach →
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="mt-20 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent z-10"></div>
            <div className="grid grid-cols-3 gap-4 max-w-5xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tri-planner-1/artifacts/cqr035t3_IMG_4594.jpeg" 
                  alt="Natación" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#00f0ff]/40 to-transparent"></div>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] transform translate-y-8 hover:translate-y-4 transition-transform duration-500">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tri-planner-1/artifacts/5t1msnm0_9659794E-562E-4F9C-A62F-921BD69A6043.jpeg" 
                  alt="Ciclismo" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#ff4d00]/40 to-transparent"></div>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tri-planner-1/artifacts/k7ciqzie_IMG_0283.jpeg" 
                  alt="Meta" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#ccff00]/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Todo lo que necesitas</h2>
            <p className="text-zinc-400 text-lg">Herramientas diseñadas para triatletas de todos los niveles</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`glass-card p-8 card-hover border ${feature.borderColor}`}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl ${feature.bgColor} flex items-center justify-center mb-6 ${feature.color}`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 px-6 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Planes de Entrenamiento</h2>
            <p className="text-zinc-400 text-lg">Desde tu primer triatlón hasta el Ironman, pasando por Aquabike y Maratón</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={plan.name}
                className="glass-card p-6 card-hover group"
                data-testid={`plan-preview-${index}`}
              >
                <div className="text-[#ccff00] font-mono text-sm mb-2">{plan.weeks}</div>
                <h3 className="text-2xl font-black mb-4 group-hover:text-[#ccff00] transition-colors">{plan.name}</h3>
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  {plan.disciplines.includes('swim') && <Waves className="w-4 h-4 text-swim" />}
                  {plan.disciplines.includes('bike') && <Bike className="w-4 h-4 text-bike" />}
                  {plan.disciplines.includes('run') && <Timer className="w-4 h-4 text-run" />}
                  <span>{plan.distance}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscriptions Comparison Section */}
      <section className="py-20 px-6" data-testid="subscriptions-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 mb-6">
              <Sparkles className="w-4 h-4 text-[#ccff00]" />
              <span className="text-sm text-zinc-400">Suscripciones</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Elige tu plan</h2>
            <p className="text-zinc-400 text-lg">Desde plantillas automáticas hasta coaching 1-a-1</p>
          </div>

          {/* Group 1: Standard Templates */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-zinc-800"></div>
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Templates automáticos</span>
              <div className="h-px flex-1 bg-zinc-800"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Mensual */}
              <div
                className="glass-card p-8 card-hover border border-zinc-800 relative"
                data-testid="landing-plan-monthly"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-black mb-2">Mensual</h3>
                  <p className="text-zinc-400 text-sm mb-4">Acceso a todos los planes</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black">9,99€</span>
                    <span className="text-zinc-400">/mes</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Todos los planes de entrenamiento',
                    'Estadísticas avanzadas',
                    'Calendario ilimitado',
                    'Soporte prioritario del coach'
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full btn-secondary" data-testid="cta-plan-monthly">
                    Empezar Mensual
                  </Button>
                </Link>
              </div>

              {/* Anual */}
              <div
                className="glass-card p-8 card-hover border-2 border-[#ccff00]/50 relative shadow-[0_0_40px_-10px_rgba(204,255,0,0.3)]"
                data-testid="landing-plan-yearly"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#ccff00] text-black text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                  <Star className="w-3 h-3 fill-black" /> Más popular
                </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-black mb-2">Anual</h3>
                  <p className="text-zinc-400 text-sm mb-4">Ahorra 30€ vs. mensual</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black">89,99€</span>
                    <span className="text-zinc-400">/año</span>
                  </div>
                  <div className="mt-2 text-xs text-[#ccff00] font-mono">≈ 7,49€/mes · 2 meses gratis</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Todo lo del plan mensual',
                    '2 meses gratis',
                    'Acceso anticipado a nuevas funciones'
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full btn-primary" data-testid="cta-plan-yearly">
                    Empezar Anual
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Group 2: Custom Coach */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-zinc-800"></div>
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Coaching personalizado 1-a-1</span>
              <div className="h-px flex-1 bg-zinc-800"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Básico */}
              <div
                className="glass-card p-8 card-hover border border-zinc-800 relative"
                data-testid="landing-plan-custom-basic"
              >
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center mb-4">
                    <Dumbbell className="w-6 h-6 text-zinc-300" />
                  </div>
                  <h3 className="text-2xl font-black mb-2">Básico</h3>
                  <p className="text-zinc-400 text-sm mb-4">Plan a medida actualizado semanalmente</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black">75€</span>
                    <span className="text-zinc-400">/mes</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Plan personalizado actualizado semanalmente',
                    'Acceso a estadísticas avanzadas',
                    'Respuesta a comentarios'
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full btn-secondary" data-testid="cta-plan-custom-basic">
                    Solicitar Básico
                  </Button>
                </Link>
              </div>

              {/* Avanzado */}
              <div
                className="glass-card p-8 card-hover border-2 border-[#00f0ff]/40 relative shadow-[0_0_40px_-10px_rgba(0,240,255,0.3)]"
                data-testid="landing-plan-custom-advanced"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#00f0ff] text-black text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                  <Star className="w-3 h-3 fill-black" /> Recomendado
                </div>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#00f0ff]/10 flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-[#00f0ff]" />
                  </div>
                  <h3 className="text-2xl font-black mb-2">Avanzado</h3>
                  <p className="text-zinc-400 text-sm mb-4">Ajustes semanales y análisis</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black">100€</span>
                    <span className="text-zinc-400">/mes</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Todo lo del Básico',
                    'Ajustes semanales según progreso',
                    'Respuesta a comentarios prioritaria',
                    'Análisis de tus actividades',
                    'Videollamada quincenal con el coach'
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#00f0ff] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full btn-primary" data-testid="cta-plan-custom-advanced">
                    Solicitar Avanzado
                  </Button>
                </Link>
              </div>

              {/* Élite */}
              <div
                className="glass-card p-8 card-hover border border-[#ff4d00]/30 relative bg-gradient-to-b from-[#ff4d00]/5 to-transparent"
                data-testid="landing-plan-custom-elite"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-[#ff4d00] to-[#ffaa00] text-black text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                  <Crown className="w-3 h-3 fill-black" /> Premium
                </div>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#ff4d00]/10 flex items-center justify-center mb-4">
                    <Crown className="w-6 h-6 text-[#ff4d00]" />
                  </div>
                  <h3 className="text-2xl font-black mb-2">Élite</h3>
                  <p className="text-zinc-400 text-sm mb-4">Coaching 1-a-1 con videollamadas</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black">150€</span>
                    <span className="text-zinc-400">/mes</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Todo lo del Avanzado',
                    'Test de rendimiento y zonas (ritmos / FTP / FC)',
                    'Entrenamientos ajustados por zonas',
                    'Videollamada quincenal con el coach',
                    'Plan nutricional básico',
                    'Soporte 1-a-1 ilimitado (chat directo)'
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#ff4d00] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full btn-primary" data-testid="cta-plan-custom-elite">
                    Solicitar Élite
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="mt-20 mb-16" data-testid="testimonials-section">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 mb-4">
                <Star className="w-4 h-4 text-[#ccff00] fill-[#ccff00]" />
                <span className="text-sm text-zinc-400">Testimonios reales</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-black mb-3">Triatletas que ya entrenan con Coach Alex</h3>
              <p className="text-zinc-400">Resultados reales de atletas reales</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  initials: 'MR',
                  name: 'Marta Ruiz',
                  location: 'Valencia',
                  plan: 'Élite',
                  planColor: 'bg-[#ff4d00]/15 text-[#ff4d00] border-[#ff4d00]/30',
                  avatarBg: 'bg-gradient-to-br from-[#ff4d00] to-[#ffaa00]',
                  achievement: 'Finisher Ironman Lanzarote 2025',
                  quote: 'Alex me preparó durante 9 meses para mi primer Ironman. El plan personalizado y las videollamadas quincenales fueron clave. Crucé la meta en 12h 47min cuando mi objetivo eran 13h.',
                  metric: '12h 47min',
                  metricLabel: 'Ironman Lanzarote'
                },
                {
                  initials: 'DG',
                  name: 'David García',
                  location: 'Madrid',
                  plan: 'Avanzado',
                  planColor: 'bg-[#00f0ff]/15 text-[#00f0ff] border-[#00f0ff]/30',
                  avatarBg: 'bg-gradient-to-br from-[#00f0ff] to-[#0088aa]',
                  achievement: 'PB en Half Ironman de Mallorca',
                  quote: 'Llevaba 3 años estancado en 5h 30min. Con los ajustes semanales y el análisis de actividades de Coach Alex bajé a 4h 58min en 6 meses. Increíble.',
                  metric: '-32 min',
                  metricLabel: 'PB Half Ironman'
                },
                {
                  initials: 'LF',
                  name: 'Laura Fernández',
                  location: 'Barcelona',
                  plan: 'Anual',
                  planColor: 'bg-[#ccff00]/15 text-[#ccff00] border-[#ccff00]/30',
                  avatarBg: 'bg-gradient-to-br from-[#ccff00] to-[#88aa00]',
                  achievement: 'Primer triatlón Sprint',
                  quote: 'Empecé desde cero, no sabía ni nadar a crol. En 8 semanas con el plan Sprint terminé mi primer triatlón sonriendo. La app es super clara y el calendario me mantuvo enfocada.',
                  metric: '1:32:14',
                  metricLabel: 'Triatlón Sprint Barcelona'
                }
              ].map((t, idx) => (
                <div
                  key={t.name}
                  className="glass-card p-6 card-hover border border-zinc-800 relative flex flex-col"
                  data-testid={`testimonial-${idx}`}
                >
                  <Quote className="absolute top-4 right-4 w-8 h-8 text-zinc-800" />
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`testimonial-star-${i}`} className="w-4 h-4 text-[#ccff00] fill-[#ccff00]" />
                    ))}
                  </div>
                  {/* Quote */}
                  <p className="text-zinc-300 text-sm leading-relaxed mb-6 flex-1">
                    “{t.quote}”
                  </p>
                  {/* Metric highlight */}
                  <div className="mb-5 p-3 rounded-xl bg-zinc-900/70 border border-zinc-800">
                    <div className="text-2xl font-black text-white font-mono">{t.metric}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">{t.metricLabel}</div>
                  </div>
                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-zinc-800">
                    <div className={`w-11 h-11 rounded-full ${t.avatarBg} flex items-center justify-center font-black text-black text-sm flex-shrink-0`}>
                      {t.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{t.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{t.location} · {t.achievement}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${t.planColor} flex-shrink-0`}>
                      {t.plan}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust bar */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-zinc-500 text-sm">
              <div className="flex items-center gap-2" data-testid="trust-rating">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`trust-star-${i}`} className="w-4 h-4 text-[#ccff00] fill-[#ccff00]" />
                  ))}
                </div>
                <span className="font-bold text-white">4,9/5</span>
                <span>en 230+ atletas</span>
              </div>
              <div className="flex items-center gap-2" data-testid="trust-finishers">
                <span className="text-2xl font-black text-white">+180</span>
                <span>finishers Ironman & Half</span>
              </div>
              <div className="flex items-center gap-2" data-testid="trust-experience">
                <span className="text-2xl font-black text-white">12 años</span>
                <span>de experiencia como coach</span>
              </div>
            </div>
          </div>

          {/* Feature Comparison Matrix (desktop only) */}
          <div className="hidden lg:block mt-16" data-testid="plan-comparison-table">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-black mb-2">Compara los 6 planes</h3>
              <p className="text-zinc-400 text-sm">Encuentra el que mejor se adapta a tu nivel</p>
            </div>
            <div className="glass-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-5 px-6 font-bold text-zinc-400 uppercase text-xs tracking-wider">Función</th>
                    <th className="py-5 px-4 text-center font-bold">Gratis<br/><span className="text-zinc-500 font-mono text-xs">0€</span></th>
                    <th className="py-5 px-4 text-center font-bold">Mensual<br/><span className="text-[#ccff00] font-mono text-xs">9,99€/mes</span></th>
                    <th className="py-5 px-4 text-center font-bold bg-[#ccff00]/5">Anual<br/><span className="text-[#ccff00] font-mono text-xs">89,99€/año</span></th>
                    <th className="py-5 px-4 text-center font-bold">Básico<br/><span className="text-zinc-300 font-mono text-xs">75€/mes</span></th>
                    <th className="py-5 px-4 text-center font-bold bg-[#00f0ff]/5">Avanzado<br/><span className="text-[#00f0ff] font-mono text-xs">100€/mes</span></th>
                    <th className="py-5 px-4 text-center font-bold">Élite<br/><span className="text-[#ff4d00] font-mono text-xs">150€/mes</span></th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Planes de entrenamiento template', vals: ['Vista previa', true, true, true, true, true] },
                    { label: 'Estadísticas avanzadas', vals: [false, true, true, true, true, true] },
                    { label: 'Calendario ilimitado', vals: [false, true, true, true, true, true] },
                    { label: 'Soporte del coach', vals: ['—', 'Prioritario', 'Prioritario', 'Prioritario', 'Prioritario', '1-a-1 ilimitado'] },
                    { label: 'Análisis de rendimiento personalizado', vals: [false, false, false, false, true, true] },
                    { label: 'Plan personalizado por el coach', vals: [false, false, false, true, true, true] },
                    { label: 'Frecuencia de actualización del plan', vals: ['—', '—', '—', 'Semanal', 'Semanal', 'Semanal'] },
                    { label: 'Test de rendimiento y zonas (FTP/FC/ritmos)', vals: [false, false, false, false, false, true] },
                    { label: 'Análisis de tus actividades', vals: [false, false, false, false, true, true] },
                    { label: 'Videollamada quincenal con coach', vals: [false, false, false, false, true, true] },
                    { label: 'Plan nutricional básico', vals: [false, false, false, false, false, true] },
                    { label: 'Chat directo ilimitado', vals: [false, false, false, false, false, true] }
                  ].map((row, idx) => (
                    <tr key={row.label} className="border-b border-zinc-800/50 hover:bg-white/[0.02]" data-testid={`compare-row-${idx}`}>
                      <td className="py-4 px-6 text-zinc-300">{row.label}</td>
                      {row.vals.map((v, i) => (
                        <td
                          key={`${row.label}-col-${i}`}
                          className={`py-4 px-4 text-center ${i === 2 ? 'bg-[#ccff00]/5' : ''} ${i === 4 ? 'bg-[#00f0ff]/5' : ''}`}
                        >
                          {v === true ? (
                            <Check className="w-5 h-5 text-[#ccff00] inline" />
                          ) : v === false ? (
                            <Minus className="w-5 h-5 text-zinc-700 inline" />
                          ) : (
                            <span className="text-zinc-300 text-xs font-medium">{v}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture - Consulta Gratuita */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="lead-form-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-[#ccff00]/10 rounded-full blur-[140px] -translate-y-1/2"></div>
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-[#00f0ff]/10 rounded-full blur-[140px] -translate-y-1/2"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            {/* Left: pitch */}
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 mb-6">
                <Send className="w-3.5 h-3.5 text-[#ccff00]" />
                <span className="text-xs text-zinc-400 uppercase tracking-widest">Consulta gratuita</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-black leading-[1.05] mb-6">
                ¿Tienes un objetivo?
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#ccff00] to-[#00f0ff]">
                  Hablemos sin compromiso.
                </span>
              </h2>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                Cuéntame tu carrera objetivo y te responderé personalmente en menos de 24h con una propuesta a medida.
              </p>
              <div className="space-y-4">
                {[
                  '100% gratis · sin compromiso',
                  'Respuesta personal del coach en 24h',
                  'Propuesta a medida para tu calendario'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-zinc-300 text-sm">
                    <div className="w-6 h-6 rounded-full bg-[#ccff00]/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#ccff00]" />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div
                  className="glass-card p-10 border border-[#ccff00]/30 text-center"
                  data-testid="lead-success-card"
                >
                  <div className="w-16 h-16 rounded-full bg-[#ccff00]/15 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-9 h-9 text-[#ccff00]" />
                  </div>
                  <h3 className="text-2xl font-black mb-3">¡Consulta enviada!</h3>
                  <p className="text-zinc-400 mb-6">
                    Te responderé personalmente desde <span className="text-white font-semibold">info@entrenadortriatlon.es</span> en menos de 24h.
                  </p>
                  <Button
                    onClick={() => { setSubmitted(false); setLead({ full_name: '', email: '', phone: '', race_distance: 'Sprint', race_date: '', goal: '' }); }}
                    variant="outline"
                    className="btn-secondary"
                    data-testid="lead-new-btn"
                  >
                    Enviar otra consulta
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleLeadSubmit}
                  className="glass-card p-8 border border-zinc-800 space-y-5"
                  data-testid="lead-form"
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Nombre completo *</label>
                      <input
                        type="text"
                        required
                        value={lead.full_name}
                        onChange={(e) => setLead({ ...lead, full_name: e.target.value })}
                        placeholder="Nombre Apellidos"
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-[#ccff00]/50 transition-colors"
                        data-testid="lead-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        placeholder="tu@email.com"
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-[#ccff00]/50 transition-colors"
                        data-testid="lead-input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        placeholder="+34 600 000 000"
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-[#ccff00]/50 transition-colors"
                        data-testid="lead-input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Fecha de carrera</label>
                      <input
                        type="date"
                        value={lead.race_date}
                        onChange={(e) => setLead({ ...lead, race_date: e.target.value })}
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-[#ccff00]/50 transition-colors"
                        data-testid="lead-input-race-date"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Distancia objetivo *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {['Sprint', 'Olímpico', 'Half Ironman', 'Ironman', 'Otra'].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setLead({ ...lead, race_distance: d })}
                          className={`px-3 py-2.5 rounded-lg text-sm font-bold transition-all ${
                            lead.race_distance === d
                              ? 'bg-[#ccff00] text-black border-2 border-[#ccff00]'
                              : 'bg-zinc-900 border-2 border-zinc-800 text-zinc-300 hover:border-zinc-600'
                          }`}
                          data-testid={`lead-distance-${d.toLowerCase().replace(' ', '-')}`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Tu objetivo *</label>
                    <textarea
                      required
                      rows={4}
                      value={lead.goal}
                      onChange={(e) => setLead({ ...lead, goal: e.target.value })}
                      placeholder="Ej: Quiero terminar mi primer Half Ironman bajo 5h, tengo 6 meses, entreno 8h/semana actualmente..."
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-[#ccff00]/50 transition-colors resize-none"
                      data-testid="lead-input-goal"
                    />
                  </div>

                  {leadError && (
                    <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="lead-error">
                      {leadError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary text-base py-6"
                    data-testid="lead-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</>
                    ) : (
                      <>Enviar consulta gratuita <Send className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  <p className="text-xs text-zinc-500 text-center">
                    Al enviar aceptas que el coach use tus datos solo para responderte. Sin spam.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6">
            ¿Listo para tu próximo
            <span className="text-[#ccff00]"> triatlón</span>?
          </h2>
          <p className="text-zinc-400 text-lg mb-10">
            Únete a miles de triatletas que ya entrenan con TriFlow Pro
          </p>
          <Link to="/register">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-final-btn">
              Empezar Ahora - Es Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/coach-logo-dark.png"
                alt="Coach Alex Díaz"
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold">Coach Alex Díaz</span>
            </div>
            <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} Coach Alex Díaz. Todos los derechos reservados.</p>
          </div>
          <div className="flex items-center justify-center sm:justify-end gap-3 flex-wrap text-xs text-zinc-500 pt-3 border-t border-zinc-900">
            <Link to="/aviso-legal" className="hover:text-zinc-300 underline">Aviso legal</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-zinc-300 underline">Privacidad</Link>
            <span>·</span>
            <Link to="/cookies" className="hover:text-zinc-300 underline">Cookies</Link>
            <span>·</span>
            <Link to="/terminos" className="hover:text-zinc-300 underline">Términos</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
